import React, { useState, useEffect } from "react";
import { Modal, Select, InputNumber, Button, Table, message, Divider, Typography } from "antd";
import { platformApi } from "../../../../api";

const { Option } = Select;
const { Text, Title } = Typography;
import { Row, Col } from "antd";

const WithdrawalModal = ({ open, close, branchOptions, emitData, record }) => {
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [denominations, setDenominations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [withdrawalCounts, setWithdrawalCounts] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
  if (open) {
    if (record) {
      // View mode
      setSelectedBranch(record.branch?.id);
      const counts = {};
      record.denominations?.forEach(d => {
        counts[d.denomination] = d.count;
      });
      setWithdrawalCounts(counts);
      setDenominations(
        record.denominations?.map(d => ({
          denomination: d.denomination,
          count: 0,
        })) || []
      );
    } else {
      // Create mode
      const defaultBranch = branchOptions?.[0]?.id || null;
      setSelectedBranch(defaultBranch);
      setDenominations([]);
      setWithdrawalCounts({});
      if (defaultBranch) {
        fetchDenominations(defaultBranch);
      }
    }
  }
}, [open, record, branchOptions]);


  const fetchDenominations = (branchId) => {
    setLoading(true);
    platformApi
      .post(`/api/payment/denominationInventory`, { branch:[branchId] })
      .then((res) => {
        setLoading(false);
        if (res.data.code === 200 && res.data.response.code === 200) {
          // Sort by denomination descending
          const sortedData = (res.data.response.data[0]?.denominations || []).sort((a, b) => b.denomination - a.denomination);
          setDenominations(sortedData);
        } else {
          message.error("Failed to fetch denominations");
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        message.error("Error fetching denominations");
      });
  };

  const handleBranchChange = (val) => {
    setSelectedBranch(val);
    setWithdrawalCounts({});
    fetchDenominations(val);
  };

  const handleCountChange = (denomination, val, available) => {
  if (val > available) {
    message.warning(`Withdrawal count for ₹${denomination} cannot exceed available count (${available})`);
    return;
  }

  setWithdrawalCounts((prev) => ({
    ...prev,
    [denomination]: val,
  }));
};


  const calculateTotal = () => {
    return Object.entries(withdrawalCounts).reduce((acc, [denom, count]) => {
      return acc + Number(denom) * (count || 0);
    }, 0);
  };

  const handleSubmit = () => {
  if (!selectedBranch) {
    return message.warning("Please select a branch");
  }

  const invalidDenoms = denominations.filter(
    (d) => (withdrawalCounts[d.denomination] || 0) > d.count
  );

  if (invalidDenoms.length > 0) {
    return message.error("Withdrawal count cannot exceed available count for any denomination");
  }

  const payloadDenominations = Object.entries(withdrawalCounts)
    .filter(([_, count]) => count > 0)
    .map(([denom, count]) => ({
      denomination: Number(denom),
      count,
    }));

  if (payloadDenominations.length === 0) {
    return message.warning("Please enter at least one denomination count");
  }

  const totalAmount = calculateTotal();

  const payload = {
    branchId: selectedBranch,
    totalAmount,
    denominations: payloadDenominations,
  };

  setSubmitting(true);
  emitData(payload)
    .then(() => {
      setSubmitting(false);
      close();
    })
    .catch(() => {
      setSubmitting(false);
    });
};

  const columns = [
    {
      title: "Denomination",
      dataIndex: "denomination",
      key: "denomination",
      width: 150,
      render: (text) => <span>₹ {text}</span>,
    },
    {
      title: "Available Count",
      dataIndex: "count",
      key: "count",
      width: 150,
      hidden: !!record,
    },
    {
      title: "Withdrawal Count",
      key: "withdrawal",
      width: 150,
      render: (_, recordItem) => (
        <InputNumber
            min={0}
            max={record ? undefined : recordItem.count}
            value={withdrawalCounts[recordItem.denomination] || 0}
            onChange={(val) => handleCountChange(recordItem.denomination, val, recordItem.count)}
            disabled={!!record}
        />
        ),
    },
    {
      title: "Amount",
      key: "amount",
      width: 200,
      render: (_, recordItem) => {
        const count = withdrawalCounts[recordItem.denomination] || 0;
        return <span>₹ {(recordItem.denomination * count).toLocaleString()}</span>;
      },
    },
  ].filter(col => !col.hidden);

  return (
    <Modal
      title={record ? "Withdrawal Details" : "New Cash Withdrawal"}
      visible={open}
      onCancel={close}
      footer={[
        <Button key="close" onClick={close}>
          Close
        </Button>,
        !record && (
          <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
            Create Withdrawal
          </Button>
        ),
      ]}
      width={700}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 8 }}>
  <Text strong style={{ whiteSpace: "nowrap" }}>Select Branch:</Text>
  <Select
    style={{ width: 300 }}
    placeholder="Select Branch"
    value={selectedBranch}
    onChange={handleBranchChange}
    disabled={loading || !!record}
  >
    {branchOptions?.map((b) => (
      <Option key={b.id} value={b.id}>
        {b.name}
      </Option>
    ))}
  </Select>
</div>


      <Table
        dataSource={denominations}
        columns={columns}
        rowKey="denomination"
        pagination={false}
        loading={loading}
        style={{ width: "100%" }}
        />


      <Divider />
      <div style={{ textAlign: "right" }}>
        <div>Total Amount: ₹ {calculateTotal().toLocaleString()}</div>
      </div>
    </Modal>
  );
};

export default WithdrawalModal;
